/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import { getRouteInitData, TextTypes } from '@gh/common-shared';
import { FormMode, isEditableFormMode, STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableTextColumn,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  rowDragMoveEventArrayFactory,
} from '@gh/core-ui-grid';
import { assign } from 'lodash';
import { ChecklistsBusinessService } from '../../../resources';
import {
  ChklClause,
  chklClauseArr,
  chklClauseFieldState,
  chklClauseValidateField,
  getChklClauseId,
  setChklClauseText,
} from '../../../resources/checklists.model';
import { ChecklistElementType } from '@gh/builder-shared-data';
import { ChecklistsDetailFormService } from '../checklists-detail-form.service';

@Component({
  standalone: false,
             selector: 'gh-checklists-detail-items',
             templateUrl: 'checklists-detail-items.component.html',
             styles: [STYLES_FLEX_COLUMN_LAYOUT],
             changeDetection: ChangeDetectionStrategy.OnPush,
           })
export class ChecklistsDetailItemsComponent implements OnInit {
  @Input() currentElementType: Maybe<string>;
  @Input() clauses: ChklClause[] = [];
  @Input() mode: FormMode;
  @Input() focusedCell: FocusedCell;  
  @Input() isParent: boolean;
  @Input() notEditable: boolean;

  @Output() clausesChange = new EventEmitter<ChklClause[]>();
  @Output() resetFocusedCell = new EventEmitter<void>();

  definition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private business: ChecklistsBusinessService,
              private gridDefinitionFactory: GridDefinitionFactory,
              public form: ChecklistsDetailFormService) {

  }

  get editable(): boolean {
    return isEditableFormMode(this.mode);
  }

  ngOnInit(): void {
    assign(this, getRouteInitData(this.route));

    this.definition = this.buildGridDefinition();
  }

  onClauseAdd(): void {
    const newClauses = chklClauseArr.append(this.clauses, this.business.createNewClause());
    this.clausesChange.emit(newClauses);
  }

  onClauseRemove(clause: ChklClause): void {
    const newClauses = chklClauseArr.remove(this.clauses, getChklClauseId(clause));
    this.clausesChange.emit(newClauses);
  }

  onFocusedCellChange(): void {
    this.resetFocusedCell.emit();
  }

  isChecklistElementType(): boolean {
    this.currentElementType = this.form.getCurrentElementType();
    return this.currentElementType === ChecklistElementType.Checklist;
  }

  private moveClauses(from: ChklClause, to: ChklClause): void {
    const fromIndex = chklClauseArr.indexOf(this.clauses, getChklClauseId(from));
    const toIndex = chklClauseArr.indexOf(this.clauses, getChklClauseId(to));

    this.clausesChange.emit(chklClauseArr.move(this.clauses, fromIndex, toIndex));
  }

  private setClauseName(clause: ChklClause, value: string): void {
    const newClauses = chklClauseArr.merge(
      this.clauses,
      getChklClauseId(clause),
      // 1. It is important to generate only the smallest subentity for merge operation,
      // because entity clause may be not the actual one.
      // 2. We always should init entityState as undefined in order to reset errors on merge
      // if entity becomes valid
      setChklClauseText({ entityState: void 0 }, 'title', value));
    this.clausesChange.emit(newClauses);
  }

  private buildGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({
       editable: this.editable,
       getId: getChklClauseId,
       getCellDef: chklClauseFieldState,
       validate: chklClauseValidateField,
       command: (entity: ChklClause, _, value) => this.setClauseName(entity, value),
     });
    return this.gridDefinitionFactory.buildManualWithModel({
       model,
       columns: [{
         rowDrag: this.editable,
         field: 'title',
         ...buildEditableTextColumn(TextTypes.Text255, model.getCellAccessor('title')),
       }],
       options: <any>{
         animateRows: true,
         onRowDragMove: rowDragMoveEventArrayFactory(this.moveClauses.bind(this)),
       },
     });
  }
}
